import type { UIMessage } from '@ai-sdk/react'
import type { ChatMessage, ChatSnapshot, ChatToolProgress } from '@plivo/chat-client'
import type { Part } from '../utils'

export type ToolPart = Extract<Part, { type: `tool-${string}` }>

export interface OverlayMessage {
  // The rendered message this one follows; null places it first.
  afterId: string | null
  message: UIMessage
}

export interface BuddyFlowView {
  snapshot: ChatSnapshot
  // Tool parts per turn, keyed by the id of the customer message that started the turn.
  // The SDK empties `progress` when a turn completes, so these are captured as they arrive.
  toolsByTurn: ReadonlyMap<string, ToolPart[]>
  inFlightTurnId?: string
  // The customer's message shown at once, until the SDK holds its own copy (same id).
  pendingUser?: UIMessage
  overlay: readonly OverlayMessage[]
  hiddenIds: ReadonlySet<string>
}

const TOOL_STATES = {
  started: 'input-available',
  completed: 'output-available',
  failed: 'output-error',
} as const

// Progress is an append-only log per turn (started, then completed or failed), so each
// tool call collapses to its latest entry, kept in the order the calls began.
export const progressToToolParts = (progress: readonly ChatToolProgress[]): ToolPart[] => {
  const latest = new Map<string, ChatToolProgress>()
  for (const entry of progress) {
    const key = entry.toolCallId ?? entry.id
    const seen = latest.get(key)
    if (!seen || entry.sequence >= seen.sequence) latest.set(key, entry)
  }
  return [...latest.entries()].map(([toolCallId, entry]) => {
    const state = TOOL_STATES[entry.state]
    return {
      type: `tool-${entry.id}`,
      toolCallId,
      state,
      input: {},
      ...(state === 'output-available' ? { output: {} } : {}),
      ...(state === 'output-error' ? { errorText: '' } : {}),
    } as ToolPart
  })
}

const textPart = (text: string): Part => ({ type: 'text', text, state: 'done' })

const fileParts = (message: ChatMessage): Part[] =>
  'files' in message
    ? message.files.map((file) => ({
        type: 'file' as const,
        mediaType: file.mimeType,
        filename: file.name,
        url: file.url,
      }))
    : []

// Text a person reads for any message kind. The Buddy flow only sends text and AI answers;
// the rest fall back to their visible wording rather than disappearing.
const messageText = (message: ChatMessage): string => {
  switch (message.kind) {
    case 'text':
    case 'ai_answer':
      return message.text
    case 'buttons':
    case 'menu_buttons':
      return [message.title, message.description].filter(Boolean).join('\n\n')
    case 'question':
      return message.question.prompt
    case 'carousel':
    case 'multi_product':
      return message.carouselMessage ?? ''
    case 'file':
    case 'image':
    case 'video':
    case 'audio':
    case 'document':
      return message.caption ?? ''
    default:
      return ''
  }
}

const toUserMessage = (message: ChatMessage): UIMessage | null => {
  const text = messageText(message)
  const parts = [...(text ? [textPart(text)] : []), ...fileParts(message)]
  return parts.length ? { id: message.id, role: 'user', parts } : null
}

const toAssistantMessage = (message: ChatMessage, tools: ToolPart[]): UIMessage | null => {
  const text = messageText(message)
  const sources =
    message.kind === 'ai_answer'
      ? message.sources.map((source) => ({
          type: 'source-url' as const,
          sourceId: source.id,
          url: source.url,
          title: source.title,
        }))
      : []
  const parts = [...tools, ...(text ? [textPart(text)] : []), ...fileParts(message), ...sources]
  return parts.length ? { id: message.id, role: 'assistant', parts } : null
}

// The SDK does not keep its list in time order: a restored transcript can hold the answer
// before the question that settled after it. Stable, so equal timestamps keep SDK order.
const chronological = (messages: readonly ChatMessage[]): ChatMessage[] =>
  messages
    .map((message, index) => ({ message, index, at: Date.parse(message.createdAt) }))
    .sort((left, right) =>
      Number.isFinite(left.at) && Number.isFinite(right.at) && left.at !== right.at
        ? left.at - right.at
        : left.index - right.index,
    )
    .map(({ message }) => message)

const isRateLimited = (message: ChatMessage): boolean =>
  message.sender === 'customer' && message.sendDisposition === 'rejected_rate_limited'

export const toBuddyMessages = (view: BuddyFlowView): UIMessage[] => {
  const { snapshot, toolsByTurn, inFlightTurnId, pendingUser, overlay, hiddenIds } = view
  const out: UIMessage[] = []
  const placed = new Set<OverlayMessage>()
  const placeAfter = (id: string | null) => {
    for (const entry of overlay) {
      if (entry.afterId !== id || placed.has(entry)) continue
      placed.add(entry)
      if (hiddenIds.has(entry.message.id)) continue
      out.push(entry.message)
      placeAfter(entry.message.id)
    }
  }
  const push = (message: UIMessage | null) => {
    if (!message || hiddenIds.has(message.id)) return
    out.push(message)
    placeAfter(message.id)
  }
  placeAfter(null)

  // Tools of the latest customer turn go on the first assistant message that answers it.
  let pendingTools: ToolPart[] = []
  for (const message of chronological(snapshot.messages)) {
    if (message.kind === 'lifecycle' || isRateLimited(message)) continue
    if (message.sender === 'customer') {
      pendingTools = toolsByTurn.get(message.id) ?? []
      push(toUserMessage(message))
    } else {
      push(toAssistantMessage(message, pendingTools))
      pendingTools = []
    }
  }
  if (pendingUser && !snapshot.messages.some((message) => message.id === pendingUser.id)) {
    pendingTools = toolsByTurn.get(pendingUser.id) ?? []
    push(pendingUser)
  }

  // A turn still running: its tools so far plus any text streaming in. Nothing to show
  // yet means the panel's own loading row stands in for it.
  const streaming =
    snapshot.aiActivity === 'idle' ? [] : snapshot.partialMessages.map((p) => p.text)
  const inFlightTools = inFlightTurnId ? pendingTools : []
  const streamingText = streaming.join('')
  if (inFlightTurnId && (inFlightTools.length || streamingText)) {
    const streamingParts: Part[] = streamingText
      ? [{ type: 'text', text: streamingText, state: 'streaming' }]
      : []
    push({
      id: `in-flight-${inFlightTurnId}`,
      role: 'assistant',
      parts: [...inFlightTools, ...streamingParts],
    })
  }
  // A local message whose anchor is gone (hidden, or an in-flight reply that settled) goes last.
  for (const entry of overlay) {
    if (placed.has(entry)) continue
    placed.add(entry)
    if (hiddenIds.has(entry.message.id)) continue
    out.push(entry.message)
    placeAfter(entry.message.id)
  }
  return out
}

// The assistant side of one turn, for the buddy_message_sent analytics event.
export const summarizeTurn = (
  messages: readonly UIMessage[],
  turnId: string,
): { responseText: string; toolsUsed: string[]; hadError: boolean } => {
  const start = messages.findIndex((message) => message.id === turnId)
  const replies: UIMessage[] = []
  if (start !== -1) {
    for (const message of messages.slice(start + 1)) {
      if (message.role === 'user') break
      replies.push(message)
    }
  }
  const parts = replies.flatMap((message) => message.parts)
  return {
    responseText: parts
      .filter((part): part is Extract<Part, { type: 'text' }> => part.type === 'text')
      .map((part) => part.text)
      .join('\n\n'),
    toolsUsed: parts
      .filter((part) => part.type.startsWith('tool-'))
      .map((part) => part.type.slice('tool-'.length)),
    hadError: parts.some(
      (part) => part.type.startsWith('tool-') && (part as ToolPart).state === 'output-error',
    ),
  }
}
